/**
 * Synthesized sound effects via the Web Audio API (no audio files). Everything
 * runs through a shared master bus with a light convolution reverb for body and
 * space. Sounds only start after the first user gesture (mobile autoplay policy).
 *
 * The AudioContext + a dedicated music bus are exported so music.ts can share
 * them (one context, independent music volume, SFX-only reverb).
 */
import { getPrefs } from './storage.js';

let ctx: AudioContext | null = null;
let master: GainNode | null = null; // final bus → destination
let sfxBus: GainNode | null = null; // SFX dry, also feeds the reverb send
let reverb: ConvolverNode | null = null;
let reverbSend: GainNode | null = null;
let musicBus: GainNode | null = null; // music routes here (no reverb, own volume)

function makeImpulse(a: AudioContext, seconds: number, decay: number): AudioBuffer {
  const len = Math.max(1, Math.floor(a.sampleRate * seconds));
  const buf = a.createBuffer(2, len, a.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buf;
}

function buildGraph(a: AudioContext): void {
  master = a.createGain();
  master.gain.value = 0.9;
  master.connect(a.destination);

  reverb = a.createConvolver();
  reverb.buffer = makeImpulse(a, 0.7, 2.4);
  reverb.connect(master);

  reverbSend = a.createGain();
  reverbSend.gain.value = 0.16;
  reverbSend.connect(reverb);

  sfxBus = a.createGain();
  sfxBus.gain.value = 1;
  sfxBus.connect(master);
  sfxBus.connect(reverbSend);

  musicBus = a.createGain();
  musicBus.gain.value = 1;
  musicBus.connect(master);
}

function ac(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      buildGraph(ctx);
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function unlockAudio(): void {
  ac();
}

/** Shared AudioContext (creating + unlocking it if needed). For music.ts. */
export function audioContext(): AudioContext | null {
  return ac();
}

/** Destination node for background music (independent of SFX reverb). */
export function musicDestination(): GainNode | null {
  ac();
  return musicBus;
}

function enabled(): boolean {
  try {
    return getPrefs().sound;
  } catch {
    return true;
  }
}

function vol(): number {
  try {
    return getPrefs().volume;
  } catch {
    return 0.6;
  }
}

function destFor(reverbMix?: number): { node: AudioNode; extraSend?: GainNode } | null {
  const a = ac();
  if (!a || !sfxBus) return null;
  if (reverbMix && reverbSend) {
    const send = a.createGain();
    send.gain.value = reverbMix;
    send.connect(reverbSend);
    return { node: sfxBus, extraSend: send };
  }
  return { node: sfxBus };
}

function tone(freq: number, dur: number, type: OscillatorType, gain: number, slideTo?: number, reverbMix?: number, detune = 0): void {
  if (!enabled()) return;
  const a = ac();
  const dest = destFor(reverbMix);
  if (!a || !dest) return;
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.detune.value = detune;
  osc.frequency.setValueAtTime(freq, a.currentTime);
  if (slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), a.currentTime + dur);
  const peak = gain * vol();
  g.gain.setValueAtTime(0.0001, a.currentTime);
  g.gain.exponentialRampToValueAtTime(peak, a.currentTime + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + dur);
  osc.connect(g);
  g.connect(dest.node);
  if (dest.extraSend) g.connect(dest.extraSend);
  osc.start();
  osc.stop(a.currentTime + dur + 0.03);
}

function noise(dur: number, gain: number, hp = 600, lp?: number): void {
  if (!enabled()) return;
  const a = ac();
  const dest = destFor(0.1);
  if (!a || !dest) return;
  const buffer = a.createBuffer(1, Math.max(1, Math.floor(a.sampleRate * dur)), a.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = a.createBufferSource();
  src.buffer = buffer;
  const filter = a.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = hp;
  let tail: AudioNode = filter;
  if (lp) {
    const low = a.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.value = lp;
    filter.connect(low);
    tail = low;
  }
  const g = a.createGain();
  g.gain.setValueAtTime(gain * vol(), a.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + dur);
  src.connect(filter);
  tail.connect(g);
  g.connect(dest.node);
  if (dest.extraSend) g.connect(dest.extraSend);
  src.start();
  src.stop(a.currentTime + dur);
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

export const Sound = {
  hit(power: number): void {
    const p = clamp(power / 6.5, 0.1, 1);
    // Crisp contact transient.
    noise(0.03, 0.1 + p * 0.12, 1500, 6000);
    // Pitched "tok" body — two detuned layers dropping in pitch.
    const base = 150 + p * 190;
    tone(base, 0.11, 'square', 0.13, base * 0.55, 0.05, -6);
    tone(base * 1.5, 0.07, 'triangle', 0.06, base * 0.8, 0.05, 8);
  },
  wall(): void {
    // Soft woody knock.
    tone(150, 0.06, 'triangle', 0.1, 95);
    noise(0.03, 0.05, 800, 2600);
  },
  bounce(): void {
    tone(430, 0.08, 'sine', 0.13, 720, 0.15);
    tone(650, 0.05, 'triangle', 0.05, 900, 0.15);
  },
  sink(): void {
    // Bright, happy three-note arpeggio with a sparkle and a touch more reverb.
    tone(523, 0.12, 'sine', 0.2, 560, 0.3);
    setTimeout(() => tone(659, 0.12, 'sine', 0.2, 700, 0.3), 80);
    setTimeout(() => {
      tone(784, 0.22, 'sine', 0.22, 840, 0.4);
      tone(1568, 0.18, 'sine', 0.05, 1700, 0.5); // sparkle
    }, 160);
  },
  water(): void {
    // Splash + low plop.
    noise(0.32, 0.16, 280, 1800);
    tone(220, 0.18, 'sine', 0.12, 90, 0.2);
  },
  teleport(): void {
    tone(300, 0.18, 'sawtooth', 0.1, 1200, 0.35, -10);
    tone(303, 0.18, 'sawtooth', 0.08, 1240, 0.35, 12);
  },
  mine(): void {
    noise(0.4, 0.3, 180, 3000);
    tone(80, 0.32, 'square', 0.2, 38, 0.25);
    tone(120, 0.2, 'triangle', 0.12, 50, 0.25);
  },
  ui(): void {
    tone(660, 0.05, 'sine', 0.06, undefined, 0.08);
  },
  win(): void {
    // Fuller ascending fanfare with a sustained chord underneath.
    [523, 659, 784, 1046].forEach((f, i) => setTimeout(() => tone(f, 0.2, 'sine', 0.18, undefined, 0.3), i * 110));
    setTimeout(() => {
      tone(523, 0.5, 'triangle', 0.08, undefined, 0.4);
      tone(659, 0.5, 'triangle', 0.07, undefined, 0.4);
      tone(784, 0.5, 'triangle', 0.07, undefined, 0.4);
    }, 440);
  },
  lose(): void {
    [392, 330, 262].forEach((f, i) => setTimeout(() => tone(f, 0.24, 'triangle', 0.16, f * 0.97, 0.25), i * 140));
  },
};
